import React, { useState, useRef, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import Peer from "peerjs";
import moment from "moment";
import {
  faPhoneSlash,
  faSyncAlt,
  faMicrophoneAltSlash,
  faMicrophoneAlt,
  faVideoSlash,
  faVideo,
  faComment,
} from "@fortawesome/free-solid-svg-icons";

import Camera from "../general/Camera";
import ButtonIcon from "../general/ButtonIcon";
import ChatPanel from "../general/ChatPanel";

import axiosConfig from "../../utils/axiosConfig";
import { getPeerConfig } from "../../utils/configPeer";
import {
  cameraErrorMessage,
  constraints as defaultConstraints,
  reverseCamera,
  startCamera,
  stopCamera,
  updateAudio,
  updateVideo,
  waitForLiveFrame,
} from "../../utils/camera";
import { setVideoBitrate, preferVP9Codec } from "../../utils/webrtcQuality";

const VisioConferenceView = ({
  appointment,
  onEndCall,
  onGoBack,
  autoStart = false,
}) => {
  const { token } = useParams();
  const cameraRef = useRef();
  const localVideoRef = useRef();
  const remoteVideoRef = useRef();
  const connRef = useRef(null);
  const statsIntervalRef = useRef(null);
  const prevBytesSentRef = useRef();
  const prevTimestampRef = useRef();

  const [constraints, setConstraints] = useState(defaultConstraints);
  const [isCamera, setIsCamera] = useState(false);
  const [isCall, setIsCall] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraDisabled, setIsCameraDisabled] = useState(false);
  const [idUser, setIdUser] = useState(null);
  const [isLoad, setIsLoad] = useState(true);
  const [localVideoWidth, setLocalVideoWidth] = useState(null);
  const [localVideoHeight, setLocalVideoHeight] = useState(null);
  const [remoteVideoWidth, setRemoteVideoWidth] = useState(null);
  const [remoteVideoHeight, setRemoteVideoHeight] = useState(null);
  const [peer, setPeer] = useState(null);
  const callRef = useRef(null);
  const [isStarting, setIsStarting] = useState(false);
  const [connectionLost, setConnectionLost] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState(null);
  const [countLoadPicture, setCountLoadPicture] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [lastReceivedMessage, setLastReceivedMessage] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isRecordAsked, setIsRecordAsked] = useState(false);
  const [startError, setStartError] = useState(null);
  const autoStartedRef = useRef(false);

  const monitorAudioQuality = useCallback((peerConnection) => {
    // Détection Safari iOS
    const isSafariIOS = /iPhone|iPad|iPod/.test(navigator.userAgent) &&
                        /Safari/.test(navigator.userAgent) &&
                        !/CriOS|FxiOS|OPiOS|mercury/.test(navigator.userAgent);

    if (!isSafariIOS) return;

    // Surveillance spécifique pour Safari iOS toutes les 5 secondes
    const audioMonitor = setInterval(async () => {
      try {
        const stats = await peerConnection.getStats();
        let audioIssues = false;

        stats.forEach((report) => {
          if (report.type === "inbound-rtp" && report.kind === "audio") {
            // Détecter les problèmes audio
            const packetsLost = report.packetsLost || 0;
            const packetsReceived = report.packetsReceived || 0;
            const lossRate = packetsReceived > 0 ? (packetsLost / packetsReceived) * 100 : 0;

            if (lossRate > 5) {
              console.warn("Safari iOS: Perte de paquets audio élevée:", lossRate.toFixed(2) + "%");
              audioIssues = true;
            }
          }

          if (report.type === "outbound-rtp" && report.kind === "audio") {
            // Vérifier que l'audio est bien envoyé
            const bytesSent = report.bytesSent || 0;
            if (bytesSent === 0) {
              console.warn("Safari iOS: Aucun audio envoyé");
              audioIssues = true;
            }
          }
        });

        // Si problèmes détectés, réactiver le track audio
        if (audioIssues && window.stream) {
          const audioTrack = window.stream.getAudioTracks()[0];
          if (audioTrack && audioTrack.enabled) {
            console.log("Safari iOS: Tentative de réactivation du track audio");
            audioTrack.enabled = false;
            setTimeout(() => {
              audioTrack.enabled = true;
            }, 100);
          }
        }
      } catch (error) {
        console.error("Erreur lors de la surveillance audio Safari iOS:", error);
      }
    }, 5000);

    // Nettoyer l'intervalle lors de la fermeture
    peerConnection.addEventListener("connectionstatechange", () => {
      if (peerConnection.connectionState === "closed" ||
          peerConnection.connectionState === "failed") {
        clearInterval(audioMonitor);
      }
    });
  }, []);

  const checkConnectionQuality = useCallback(async () => {
    if (!callRef.current || !callRef.current.peerConnection) return;

    const stats = await callRef.current.peerConnection.getStats();
    let quality = {
      bitrateKbps: null,
      packetLoss: null,
      rtt: null,
      jitter: null,
    };

    stats.forEach((report) => {
      // RTT
      if (report.type === "candidate-pair" && report.state === "succeeded") {
        quality.rtt = (report.currentRoundTripTime * 1000).toFixed(0); // ms
      }
      // Audio
      if (report.type === "inbound-rtp" && report.kind === "audio") {
        quality.jitter = (report.jitter * 1000).toFixed(0); // ms
        quality.packetLoss = report.packetsLost;
      }
      // Bitrate vidéo
      if (report.type === "outbound-rtp" && report.kind === "video") {
        if (
          prevBytesSentRef.current !== undefined &&
          prevTimestampRef.current !== undefined
        ) {
          const deltaBytes = report.bytesSent - prevBytesSentRef.current;
          const deltaTime = report.timestamp - prevTimestampRef.current;
          const bitrate = (deltaBytes * 8) / (deltaTime / 1000);
          quality.bitrateKbps = (bitrate / 1000).toFixed(1);
        }
        prevBytesSentRef.current = report.bytesSent;
        prevTimestampRef.current = report.timestamp;
      }
    });

    setConnectionQuality(quality);
  }, [callRef]);

  const listenToCallStream = useCallback(
    (incomingCall) => {
      incomingCall.on("stream", (remoteStream) => {
        console.log(remoteStream);

        remoteVideoRef.current.srcObject = remoteStream;
        remoteVideoRef.current.addEventListener(
          "playing",
          () => {
            setRemoteVideoWidth(remoteVideoRef.current.videoWidth);
            setRemoteVideoHeight(remoteVideoRef.current.videoHeight);
            setIsCall(true);

            // Démarrer la vérification de la qualité toutes les 2 secondes
            if (!statsIntervalRef.current) {
              statsIntervalRef.current = setInterval(
                checkConnectionQuality,
                2000
              );
            }
          },
          { once: true }
        );
      });

      incomingCall.peerConnection.oniceconnectionstatechange = () => {
        const state = incomingCall.peerConnection.iceConnectionState;
        console.log("Connection state:", state);

        // Détecter la perte de connexion
        if (
          state === "disconnected" ||
          state === "failed" ||
          state === "closed"
        ) {
          console.warn("Connexion perdue :", state);
          setConnectionLost(true);
          // Nettoyer l'intervalle de stats
          if (statsIntervalRef.current) {
            clearInterval(statsIntervalRef.current);
            statsIntervalRef.current = null;
          }
          setConnectionQuality(null);
        } else if (state === "connected" || state === "completed") {
          setConnectionLost(false);
          setVideoBitrate(incomingCall.peerConnection, 2_500_000);
        }
      };
    },
    [checkConnectionQuality]
  );

  useEffect(() => {
    const getFolderClientData = async () => {
      try {
        const res = await axiosConfig("folder/get-folder-client", {
          data: { token },
        });
        if (res.data.success) {
          const { folder } = res.data;
          setIdUser(folder.idUser);
          setIsLoad(false);
          if (folder.status < 4) {
            // Ne plus créer le peer ici, il sera créé dans handleStartClick
            console.log("Dossier prêt, ID utilisateur:", folder.idUser);
          } else {
            alert("Le dossier est clôturé");
          }
        } else {
          alert("Erreur lors de la récupération du dossier");
          setIsLoad(false);
        }
      } catch (err) {
        alert("Erreur lors de la récupération du dossier");
        setIsLoad(false);
      }
    };

    getFolderClientData();
  }, [token]);

  const insertPictureClient = (data) => {
    axiosConfig("folder/insert-picture-client", {
      data: {
        base64: data,
        token: token,
      },
    })
      .then((res) => {
        if (res.data.success) {
          setCountLoadPicture(countLoadPicture - 1);
          if (connRef.current) {
            connRef.current.send({
              type: "return-picture",
              id: res.data.id,
            });
          } else {
            alert("Erreur connexion");
          }
        } else {
          alert("Une erreur est survenue");
        }
      })
      .catch((e) => {
        alert(e);
        alert("Une erreur est survenue");
      });
  };

  const handleTakePictureClick = useCallback(async () => {
    const video = localVideoRef.current;

    if (!(await waitForLiveFrame(video, window.stream))) {
      if (connRef.current) connRef.current.send({ type: "picture-error" });
      return;
    }

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    const width =
      video.videoWidth > video.videoHeight
        ? 720
        : (720 * video.videoWidth) / video.videoHeight;
    const height =
      video.videoWidth > video.videoHeight
        ? (720 * video.videoHeight) / video.videoWidth
        : 720;

    canvas.width = width;
    canvas.height = height;

    context.drawImage(video, 0, 0, width, height);
    const date = moment().format("DD/MM/YYYY HH:mm:ss");
    context.fillStyle = "red";
    context.font = "16px Arial";
    context.fillText(date, 20, 40);

    const imageData = canvas.toDataURL("image/jpeg", 0.8);
    setCountLoadPicture(countLoadPicture + 1);

    insertPictureClient(imageData);
  }, [countLoadPicture]);

  const handleReverseCameraClick = useCallback(() => {
    reverseCamera(constraints, (newConstraints) => {
      setConstraints(newConstraints);
      startCamera(newConstraints, (result) => {
        if (result.success) {
          window.stream = result.stream;
          localVideoRef.current.srcObject = result.stream;

          // Remplacer le track vidéo
          const videoSender = callRef.current.peerConnection
            .getSenders()
            .find((s) => s.track.kind === "video");
          if (videoSender) videoSender.replaceTrack(window.stream.getVideoTracks()[0]);

          // Remplacer le track audio (sinon le micro ne fonctionne plus)
          const audioSender = callRef.current.peerConnection
            .getSenders()
            .find((s) => s.track.kind === "audio");
          if (audioSender) audioSender.replaceTrack(window.stream.getAudioTracks()[0]);
        }
      });
    });
  }, [constraints]);

  const handleStopCameraClick = useCallback((notifyExpert = false) => {
    stopCamera(() => {
      setIsCamera(false);
      setIsCall(false);
      setConnectionLost(false);

      if (notifyExpert && connRef.current)
        connRef.current.send({ type: "close-call" });

      // Fermer l'appel
      if (callRef.current) {
        callRef.current.close();
        callRef.current = null;
      }

      // Fermer la connexion de données
      if (connRef.current) {
        connRef.current.close();
        connRef.current = null;
      }

      // IMPORTANT : Détruire le peer pour libérer l'ID
      if (peer && !peer.destroyed) {
        console.log("Destruction du peer pour libérer l'ID");
        peer.destroy();
        setPeer(null);
      }

      // Nettoyer les refs vidéo
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = null;
      }
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = null;
      }

      // Réinitialiser les dimensions vidéo
      setLocalVideoWidth(null);
      setLocalVideoHeight(null);
      setRemoteVideoWidth(null);
      setRemoteVideoHeight(null);

      // Réinitialiser les états audio/vidéo
      setIsMuted(false);
      setIsCameraDisabled(false);
      setIsRecording(false);
      setIsRecordAsked(false);

      // Réinitialiser la qualité de connexion
      if (statsIntervalRef.current) {
        clearInterval(statsIntervalRef.current);
        statsIntervalRef.current = null;
      }
      setConnectionQuality(null);

      // Retour vers la confirmation
      onEndCall();
    });
  }, [peer, onEndCall]);

  const handlePeerData = useCallback((data) => {
    switch (data.type) {
      case "reject-call":
        alert("L'expert est indisponible");
        handleStopCameraClick(false);
        break;
      case "close-call":
        handleStopCameraClick(false);
        break;
      case "take-picture":
        handleTakePictureClick();
        break;
      case "reverse-camera":
        handleReverseCameraClick();
        break;
      case "point-marker":
        if (cameraRef.current) {
          cameraRef.current.addMarker(data.x, data.y, data.width, data.height);
        }
        break;
      case "clear-marker":
        if (cameraRef.current) {
          cameraRef.current.clearCanvas();
        }
        break;
      case "save-picture":
        insertPictureClient(data.base64);
        break;
      case "zoom":
        if (window.stream) {
          const videoTrack = window.stream.getVideoTracks()[0];
          if (videoTrack) {
            const capabilities = videoTrack.getCapabilities();
            if (capabilities.zoom) {
              const minZoom = capabilities.zoom.min;
              const maxZoom = capabilities.zoom.max;
              const targetZoom = Math.min(
                Math.max(data.level, minZoom),
                maxZoom
              );
              videoTrack.applyConstraints({
                advanced: [{ zoom: targetZoom }],
              });
            }
          }
        }
        break;
      case "record-request":
        setIsRecordAsked(true);
        break;
      case "record-started":
        setIsRecordAsked(false);
        setIsRecording(true);
        break;
      case "record-stopped":
        setIsRecordAsked(false);
        setIsRecording(false);
        break;
      case "chat-message":
        setChatMessages((prev) => [...prev, data]);
        setIsChatOpen((open) => {
          if (!open) {
            setUnreadChatCount((c) => c + 1);
            setLastReceivedMessage(data);
          }
          return open;
        });
        break;
      default:
        console.warn("Unhandled data type:", data.type);
        break;
    }
  }, [handleStopCameraClick, handleTakePictureClick, handleReverseCameraClick]);

  const handleRecordConsent = useCallback((accepted) => {
    setIsRecordAsked(false);
    if (connRef.current) {
      connRef.current.send({ type: "record-consent", accepted });
    }
  }, []);

  const handleRecordRevoke = useCallback(() => {
    setIsRecording(false);
    if (connRef.current) {
      connRef.current.send({ type: "record-revoke" });
    }
  }, []);

  const handleSendChatMessage = useCallback(
    (text) => {
      if (!text.trim() || !connRef.current) return;
      const message = {
        type: "chat-message",
        text: text.trim(),
        sender: "client",
        timestamp: Date.now(),
      };
      connRef.current.send(message);
      setChatMessages((prev) => [...prev, message]);
    },
    []
  );

  const handleToggleChat = useCallback(() => {
    setIsChatOpen((prev) => {
      if (!prev) {
        setUnreadChatCount(0);
        setLastReceivedMessage(null);
      }
      return !prev;
    });
  }, []);

  const handleStartClick = useCallback(async () => {
    // Protection contre les appels multiples
    if (isStarting || isCamera) {
      console.log("Démarrage déjà en cours ou caméra déjà active");
      return;
    }

    setIsStarting(true);
    setStartError(null);

    // Si on avait déjà un peer, on le nettoie avant de recommencer

    if (peer && !peer.destroyed) {
      peer.destroy();
    }

    // Recréer la connexion peer
    const config = await getPeerConfig(token);
    const newPeer = new Peer(`${token}_client`, config);

    newPeer.on("open", () => {
      console.log("Peer ouvert avec ID:", newPeer.id);
      setPeer(newPeer);

      // Maintenant démarrer la caméra avec les contraintes actuelles ou par défaut
      const cameraConstraints = constraints || defaultConstraints;
      console.log("Utilisation des contraintes:", cameraConstraints);

      startCamera(cameraConstraints, (result) => {
        if (result.success) {
          window.stream = result.stream;
          console.log("Caméra démarrée avec succès");

          // Détection Safari iOS - Forcer la configuration audio
          const isSafariIOS = /iPhone|iPad|iPod/.test(navigator.userAgent) &&
                              /Safari/.test(navigator.userAgent) &&
                              !/CriOS|FxiOS|OPiOS|mercury/.test(navigator.userAgent);

          if (isSafariIOS) {
            console.log("Safari iOS détecté - Application des optimisations audio");
            const audioTrack = window.stream.getAudioTracks()[0];
            if (audioTrack) {
              const settings = audioTrack.getSettings();
              console.log("Paramètres audio Safari iOS:", settings);

              // S'assurer que le track est actif
              audioTrack.enabled = true;
            }
          }

          setIsCamera(true);
          setIsStarting(false);
        } else {
          newPeer.destroy();
          setStartError(cameraErrorMessage(result.error));
          setIsStarting(false);
        }
      });
    });

    newPeer.on("call", (incomingCall) => {
      console.log("Appel entrant reçu");

      // Vérifier que le stream est disponible avant de répondre
      if (!window.stream) {
        console.error("Stream non disponible pour répondre à l'appel");
        alert("Impossible de répondre à l'appel : caméra non prête");
        return;
      }

      console.log("Réponse à l'appel avec le stream:", window.stream);

      // Optimisation Safari iOS : Forcer l'activation des tracks audio avant de répondre
      const audioTrack = window.stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = true;
        console.log("Track audio activé:", audioTrack.getSettings());
      }

      if (incomingCall.peerConnection) {
        preferVP9Codec(incomingCall.peerConnection);
      }

      incomingCall.answer(window.stream);
      callRef.current = incomingCall;
      listenToCallStream(incomingCall);

      // Surveillance de la qualité audio sur Safari iOS
      if (incomingCall.peerConnection) {
        monitorAudioQuality(incomingCall.peerConnection);
      }
    });

    newPeer.on("disconnect", () => console.log("PeerJS disconnected"));
    newPeer.on("error", (err) => {
      console.error("PeerJS error:", err);
      if (!newPeer.open) {
        setStartError(
          "La connexion à la visioconférence a échoué. Fermez les autres onglets ouverts sur ce lien, vérifiez votre connexion internet, puis réessayez."
        );
      }
      setIsStarting(false);
    });
  }, [constraints, token, peer, listenToCallStream, isStarting, isCamera, monitorAudioQuality]);

  // A single automatic attempt: retrying on failure looped forever (2.5 starts/s).
  useEffect(() => {
    if (autoStart && !autoStartedRef.current && !isLoad && idUser) {
      autoStartedRef.current = true;
      handleStartClick();
    }
  }, [autoStart, isLoad, idUser, handleStartClick]);

  useEffect(() => {
    if (isCamera && localVideoRef.current && window.stream) {
      const videoElement = localVideoRef.current;

      // Ne pas réassigner si déjà assigné
      if (!videoElement.srcObject) {
        console.log("Attribution du stream local à la vidéo");
        videoElement.srcObject = window.stream;
      }

      const handlePlaying = () => {
        console.log("Vidéo locale en cours de lecture");
        setLocalVideoWidth(videoElement.videoWidth);
        setLocalVideoHeight(videoElement.videoHeight);

        if (peer && idUser && !connRef.current) {
          // Connecter au peer expert (que ce soit la première fois ou une reconnexion)
          console.log("Connexion au peer expert");
          connRef.current = peer.connect(`xpertvisio_${idUser}_expert`);
          connRef.current.on("open", () => {
            console.log("Connexion ouverte avec l'expert");
            connRef.current.on("data", handlePeerData);
            // Envoi du message call même si on a déjà été en call avant
            console.log("Envoi du message 'call' à l'expert");
            connRef.current.send({ type: "call", token });
            setIsCall(true);
          });
        }
      };

      if (videoElement.paused) {
        videoElement.addEventListener("playing", handlePlaying, { once: true });
        return () => {
          videoElement.removeEventListener("playing", handlePlaying);
        };
      } else {
        // Si déjà en lecture, appeler directement handlePlaying
        handlePlaying();
      }
    }
  }, [isCamera, isCall, peer, idUser, token, handlePeerData]);

  // Nettoyage lors du démontage du composant
  useEffect(() => {
    return () => {
      // Nettoyer le stream vidéo
      if (window.stream) {
        window.stream.getTracks().forEach((track) => track.stop());
        window.stream = null;
      }

      // Fermer les connexions
      if (callRef.current) {
        callRef.current.close();
      }
      if (connRef.current) {
        connRef.current.close();
      }
      if (peer && !peer.destroyed) {
        peer.destroy();
      }
      if (statsIntervalRef.current) {
        clearInterval(statsIntervalRef.current);
        statsIntervalRef.current = null;
      }
      setConnectionQuality(null);
    };
  }, [callRef, peer]);

  const handleUpdateAudioClick = () => {
    updateAudio(isMuted);
    setIsMuted(!isMuted);
  };

  const handleUpdateVideoClick = () => {
    updateVideo(isCameraDisabled);
    setIsCameraDisabled(!isCameraDisabled);
  };

  if (!isCamera) {
    return (
      <Card className="bg-white shadow-lg border-0 overflow-hidden">
        <div className="p-6">
          <div className="space-y-4">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                  ></path>
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Visioconférence d'expertise
              </h2>
            </div>

            {appointment && (
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="text-center space-y-1">
                  <p className="text-sm font-medium text-green-900">
                    Rendez-vous en cours
                  </p>
                  <p className="text-sm text-green-800">
                    {format(appointment.date, "EEEE d MMMM yyyy", {
                      locale: fr,
                    })}{" "}
                    à {appointment.time}
                  </p>
                  <p className="text-xs text-green-700">
                    Référence : {appointment.referenceNumber}
                  </p>
                </div>
              </div>
            )}

            {startError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800 text-center">{startError}</p>
              </div>
            )}

            <div className="text-center">
              <Button
                onClick={handleStartClick}
                disabled={isLoad || isStarting}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2"
              >
                {isStarting
                  ? "Démarrage en cours..."
                  : isLoad
                  ? "Chargement..."
                  : startError
                  ? "Réessayer"
                  : "Démarrer la visioconférence"}
              </Button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={onGoBack}
                className="text-xs text-gray-600 hover:text-gray-800 underline"
              >
                Retour
              </button>
            </div>
          </div>
        </div>

        {isLoad && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p>Initialisation...</p>
            </div>
          </div>
        )}
      </Card>
    );
  }

  return (
    <div className="fixed inset-0 bg-black z-50">
      {isCamera && (
        <>
          <Camera
            peer={peer}
            ref={cameraRef}
            firstVideoRef={localVideoRef}
            firstVideoWidth={localVideoWidth}
            firstVideoHeight={localVideoHeight}
            firstVideoIsMuted={true}
            secondVideoRef={remoteVideoRef}
            secondVideoWidth={remoteVideoWidth}
            secondVideoHeight={remoteVideoHeight}
            isMuted={isMuted}
            secondVideoIsMuted={false}
            connectionQuality={connectionQuality}
            isRecording={isRecording}
            onStopRecording={handleRecordRevoke}
          >
            <ButtonIcon
              icon={isMuted ? faMicrophoneAltSlash : faMicrophoneAlt}
              red={isMuted}
              onClick={handleUpdateAudioClick}
              title={isMuted ? "Réactiver le micro" : "Couper le micro"}
            />
            <ButtonIcon
              icon={isCameraDisabled ? faVideoSlash : faVideo}
              red={isCameraDisabled}
              onClick={handleUpdateVideoClick}
              title={isCameraDisabled ? "Réactiver la caméra" : "Couper la caméra"}
            />
            <ButtonIcon
              icon={faSyncAlt}
              onClick={handleReverseCameraClick}
              title="Changer de caméra"
            />
            <ButtonIcon
              onClick={handleToggleChat}
              green={isChatOpen}
              icon={faComment}
              title="Messages"
              badge={unreadChatCount > 0 ? unreadChatCount : null}
            />
            <ButtonIcon
              icon={faPhoneSlash}
              danger
              title="Raccrocher"
              onClick={() => handleStopCameraClick(true)}
            />
          </Camera>
          <ChatPanel
            messages={chatMessages}
            onSend={handleSendChatMessage}
            isOpen={isChatOpen}
            onClose={handleToggleChat}
            currentSender="client"
            lastReceivedMessage={lastReceivedMessage}
          />
        </>
      )}

      {/* Demande d'enregistrement de la visioconférence */}
      {isRecordAsked && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[260]">
          <div className="bg-white rounded-lg p-6 max-w-md text-center shadow-xl mx-4">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <div className="w-6 h-6 bg-red-600 rounded-full" />
            </div>
            <h3 className="text-lg font-semibold mb-2">
              Enregistrement de la visioconférence
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              L'expert souhaite enregistrer cette visioconférence.
              L'enregistrement sera joint à votre dossier et conservé 90 jours.
              Vous pourrez l'interrompre à tout moment.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => handleRecordConsent(false)}
              >
                Refuser
              </Button>
              <Button onClick={() => handleRecordConsent(true)}>
                Accepter
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Alerte de perte de connexion */}
      {connectionLost && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[250]">
          <div className="bg-white rounded-lg p-6 max-w-md text-center shadow-xl">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Connexion perdue
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              La connexion avec l'expert a été interrompue.
            </p>
            <div className="flex gap-2 justify-center">
              <Button
                onClick={() => {
                  setConnectionLost(false);
                  handleStopCameraClick(false);
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2"
              >
                Quitter
              </Button>
              <Button
                onClick={() => {
                  console.log("Tentative de reconnexion");
                  setConnectionLost(false);
                  // Fermer proprement l'ancienne connexion
                  if (callRef.current) callRef.current.close();
                  if (connRef.current) connRef.current.close();
                  setIsCall(false);
                  // Relancer la connexion avec l'expert
                  if (peer && idUser) {
                    connRef.current = peer.connect(
                      `xpertvisio_${idUser}_expert`
                    );
                    connRef.current.on("open", () => {
                      console.log("Reconnexion établie avec l'expert");
                      connRef.current.on("data", handlePeerData);
                      connRef.current.send({ type: "call-reconnect", token });
                    });
                  }
                }}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2"
              >
                Réessayer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisioConferenceView;
